import {
  CalculatorBuyerCostsFormFields,
  CalculatorBuyerCostsResultData,
  CalculatorsBuyerCostsStep1FormFields,
  CalculatorsBuyerCostsStep2FormFields
} from '../types';
import { utilNumberToCurrency } from '../../../../../utils';
import { ApiNhgResponse } from '../../../../../@types';
import { IntlShape } from 'react-intl/src/types';
import {
  useCalculatorBuyerCostsStep1FormFields,
  useCalculatorBuyerCostsStep2FormFields
} from '../config';

const utilsCalculatorCalculateBuyerCosts = (
  form: CalculatorBuyerCostsFormFields,
  nhg: ApiNhgResponse
): CalculatorBuyerCostsResultData => {
  const {
    housePrice,
    isNewConstruction,
    applicantAgeUnder35,
    applicantTransferTaxExemptionUsed,
    partnerAgeUnder35,
    partnerTransferTaxExemptionUsed,
    willLiveInProperty,
    withPartner
  } = form;

  const housePriceNumber = parseInt(housePrice);
  const isNewConstructionBoolean = isNewConstruction === 'true';

  const nhgAmount = isNewConstruction ? nhg.newBuilding : nhg.existingBuilding;
  const applicantAgeUnder35Boolean = applicantAgeUnder35 === 'true';
  const partnerAgeUnder35Boolean = partnerAgeUnder35 === 'true';
  const applicantTransferTaxExemptionUsedBoolean = applicantTransferTaxExemptionUsed === 'true';
  const partnerTransferTaxExemptionUsedBoolean = partnerTransferTaxExemptionUsed === 'true';
  const willLiveInPropertyBoolean = willLiveInProperty === 'true';
  const withPartnerBoolean = withPartner === 'true';

  const transferTaxExemptionPercentage = calculateTransferTaxExemptionPercentage(
    withPartnerBoolean,
    isNewConstructionBoolean,
    housePriceNumber,
    applicantAgeUnder35Boolean,
    partnerAgeUnder35Boolean,
    applicantTransferTaxExemptionUsedBoolean,
    partnerTransferTaxExemptionUsedBoolean,
    willLiveInPropertyBoolean
  );
  const transferTaxExemptionAmount = (housePriceNumber / 100) * transferTaxExemptionPercentage;
  const notaryCosts = isNewConstructionBoolean ? 0 : 1000;
  const mortgageAdviceCosts = isNewConstructionBoolean ? 0 : (housePriceNumber / 100) * 1.5;
  const valuationCosts = isNewConstructionBoolean ? 0 : 700;
  const buildingInspectionCosts = isNewConstructionBoolean ? 0 : 700;
  const nhgCosts = housePriceNumber < nhgAmount ? (housePriceNumber / 100) * 0.4 : 0;
  const adviceCosts = withPartnerBoolean
    ? willLiveInPropertyBoolean
      ? 3295
      : 3895
    : willLiveInPropertyBoolean
    ? 2795
    : 3395;

  const buyerCosts =
    transferTaxExemptionAmount +
    notaryCosts +
    mortgageAdviceCosts +
    valuationCosts +
    buildingInspectionCosts +
    nhgCosts +
    adviceCosts;

  return {
    withPartner: withPartnerBoolean,
    isNewConstruction: isNewConstructionBoolean,
    applicantAgeUnder35: applicantAgeUnder35Boolean,
    applicantTransferTaxExemptionUsed: applicantTransferTaxExemptionUsedBoolean,
    partnerAgeUnder35: partnerAgeUnder35Boolean,
    partnerTransferTaxExemptionUsed: partnerTransferTaxExemptionUsedBoolean,
    housePrice: utilNumberToCurrency(housePriceNumber, true, 0),
    buyerCosts: utilNumberToCurrency(buyerCosts, true, 0),
    transferTaxExemptionPercentage: `${transferTaxExemptionPercentage.toString()}%`,
    transferTaxExemptionAmount: utilNumberToCurrency(transferTaxExemptionAmount, true, 0),
    notaryCosts: utilNumberToCurrency(notaryCosts, true, 0),
    brokerCommission: utilNumberToCurrency(mortgageAdviceCosts, true, 0),
    valuationCosts: utilNumberToCurrency(valuationCosts, true, 0),
    buildingInspectionCosts: utilNumberToCurrency(buildingInspectionCosts, true, 0),
    nhgCosts: utilNumberToCurrency(nhgCosts, true, 0),
    adviceCosts: utilNumberToCurrency(adviceCosts, true, 0)
  };
};

const utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep1 = (
  intl: IntlShape
): CalculatorsBuyerCostsStep1FormFields => {
  const calculatorBuyerCostsStep1FormFields = useCalculatorBuyerCostsStep1FormFields(intl);
  return {
    housePrice: calculatorBuyerCostsStep1FormFields.housePrice.defaultValue,
    isNewConstruction: calculatorBuyerCostsStep1FormFields.isNewConstruction.defaultValue,
    withPartner: calculatorBuyerCostsStep1FormFields.withPartner.defaultValue
  };
};

const utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep2 = (
  intl: IntlShape
): CalculatorsBuyerCostsStep2FormFields => {
  const calculatorBuyerCostsStep2FormFields = useCalculatorBuyerCostsStep2FormFields(intl);
  return {
    applicantAgeUnder35: calculatorBuyerCostsStep2FormFields.applicantAgeUnder35.defaultValue,
    applicantTransferTaxExemptionUsed:
      calculatorBuyerCostsStep2FormFields.applicantTransferTaxExemptionUsed.defaultValue,
    partnerAgeUnder35: calculatorBuyerCostsStep2FormFields.partnerAgeUnder35.defaultValue,
    partnerTransferTaxExemptionUsed:
      calculatorBuyerCostsStep2FormFields.partnerTransferTaxExemptionUsed.defaultValue,
    willLiveInProperty: calculatorBuyerCostsStep2FormFields.willLiveInProperty.defaultValue
  };
};

const calculateTransferTaxExemptionPercentage = (
  withPartner: boolean,
  isNewConstruction: boolean,
  housePrice: number,
  applicantAgeUnder35: boolean,
  partnerAgeUnder35 = false,
  applicantTransferTaxExemptionUsed: boolean,
  partnerTransferTaxExemptionUsed = false,
  willLiveInProperty: boolean
) => {
  const priceThreshold = 440000;

  if (isNewConstruction) return 0;
  if (!willLiveInProperty) return 10.4;

  const bothUnder35 = applicantAgeUnder35 && (withPartner ? partnerAgeUnder35 : true);
  const neitherUsedExemption =
    !applicantTransferTaxExemptionUsed && (!withPartner || !partnerTransferTaxExemptionUsed);
  const underPriceThreshold = housePrice < priceThreshold;
  const oneUnder35 =
    (applicantAgeUnder35 || (withPartner && partnerAgeUnder35)) &&
    !(applicantAgeUnder35 && partnerAgeUnder35);

  // Both (or single applicant) under 35, price under threshold, neither has used exemption, and will live in the property
  if (bothUnder35 && underPriceThreshold && neitherUsedExemption && willLiveInProperty) {
    return 0;
  }

  // One under 35 (in case of a partner), the other 35 or over, price under threshold, neither has used exemption, and will live in the property
  if (
    withPartner &&
    oneUnder35 &&
    underPriceThreshold &&
    neitherUsedExemption &&
    willLiveInProperty
  ) {
    return 1;
  }

  // Default to 2% in all other cases where the property is for self-living
  return 2;
};

export {
  utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep1,
  utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep2,
  utilsCalculatorCalculateBuyerCosts
};
